import { TimeZone, isEqual } from "../utils";
import React, { useEffect, useRef, useState } from "react";
import { camelCaseToText } from "../../consts";
import DashboardApiHandler from "../dashboard/api";
import spinner from "./color_spinner.svg";
import Dropdown from "react-dropdown";
import ApiHandler from "../api";
import Modal from "../modal/modal";
import download from "../robotSettingsNew/download-icon.svg";
import upload from "../robotSettingsNew/upload-icon.svg";
import { useRobot } from "./RobotContext";

const RobotSettings = ({ project, user, batteryDataReady }) => {
  const { robot, setRobot } = useRobot();
  const [editSettings, setEditSettings] = useState(false);

  // Technicians can only edit specific fields
  const isTechnician = user.role === "technician";
  const technicianAllowedSettings = ["driveMotorType", "cleaningMotorType"];
  const canEditSetting = (settingKey) => {
    if (!editSettings) return false;
    if (!isTechnician) return true;
    return technicianAllowedSettings.includes(settingKey);
  };
  // Battery percentage and battery size are editable by technicians (when in edit mode)
  const canEditBatteryPercentage = editSettings;
  const canEditBatterySize = editSettings;
  const [showSaveSettings, setShowSaveSettings] = useState(false);
  const [showLoadDefault, setShowLoadDefault] = useState(false);
  const [isLoadingVoltages, setIsLoadingVoltages] = useState(false);
  const [batteryPercentage, setBatteryPercentage] = useState();

  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [settings, setSettings] = useState({});
  const [moreSettings, setMoreSettings] = useState({});
  const [liveReport, setLiveReport] = useState();
  const [processId, setProcessId] = useState(null);
  const [liveInterval, setLiveInterval] = useState(null);
  // File input ref for config upload
  const fileInputRef = useRef(null);

  // Motor options - hardcoded like in settings.js
  const motorOptions = {
    driveMotorOptions: [
      { value: "4.5_NM_1_23", label: "4.5 Nm (1:23)" },
      { value: "6.5_NM_1_47", label: "6.5 Nm (1:47)" },
      {
        value: "4_NM_MOB16V_3060RPM_1_51",
        label: "4 Nm MOB16V 3060RPM (1:51)",
      },
      {
        value: "6_NM_PN_MOB160665_1_59",
        label: "6 Nm PN. MOB160665 (1:59)",
      },
    ],
    cleaningMotorOptions: [
      { value: "4.5_NM_1_15", label: "4.5 Nm (1:15)" },
      {
        value: "6.5_NM_200RPM_1_18",
        label: "6.5 Nm 200rpm (1:18)",
      },
      {
        value: "6.5_NM_250RPM_1_23",
        label: "6.5 Nm 250rpm (1:23)",
      },
      {
        value: "5_NM_MOB16V_4000RPM_1_15",
        label: "5 Nm MOB16V 4000RPM (1:15)",
      },
      {
        value: "5_NM_PN_MOB1605250",
        label: "5 Nm PN.MOB1605250 (1:14)",
      },
    ],
  };

  useEffect(() => {
    getLiveReports(processId);
  }, [processId]);

  useEffect(() => {
    // getReportOnce();
  }, [batteryDataReady]);

  const dashboardApiHandler = new DashboardApiHandler();
  const apiHandler = new ApiHandler();
  const prevRobot = useRef();

  useEffect(() => {
    if (
      robot &&
      (!prevRobot.current ||
        prevRobot.current.id !== robot.id ||
        !isEqual(robot.settings, settings))
    ) {
      setSettings(robot.settings);
      setMoreSettings(robot.moreSettings || {});
      setIsLoadingVoltages(false);
      setProcessId(null);
      setLiveReport(null);
      setLiveReport(null);
      setEditSettings(false);
      console.log("robot has changed, settings live report to null");
    } else {
      console.log("robot has not changed", robot, prevRobot.current);
    }

    prevRobot.current = robot;
  }, [robot]);

  const loadDefaultSettings = () => {
    const { settings: projectSettings } = project;
    const tmpSettings = { ...settings };
    for (const key in settings) {
      if (projectSettings[key] !== settings[key]) {
        tmpSettings[key] = projectSettings[key];
      }
    }
    tmpSettings.setToDefault = true;

    setSettings(tmpSettings);
    setShowLoadDefault(false);
  };

  const refreshRobotBatteries = async () => {
    setIsLoadingVoltages(true);
    try {
      const result = await dashboardApiHandler.refreshRobotBatteries(
        project._id,
        robot.id
      );

      setProcessId(result.data.processId);
    } catch (error) {
      console.log(error);
      alert("Error getting updated robot voltage");
    }
  };

  const handleShowLoadDefault = (currentSettings) => {
    if (!currentSettings) return true;

    for (let key in project.settings) {
      if (project.settings[key] !== currentSettings[key]) return false;
    }
    return true;
  };

  const onChange = (value, param) => {
    const tmpSettings = { ...settings };
    tmpSettings[param] = value;
    if (!isNaN(Number(value))) {
      tmpSettings[param] = Number(value);
    }
    tmpSettings.setToDefault = false;

    setSettings(tmpSettings);
    setShowLoadDefault(!handleShowLoadDefault(tmpSettings));
  };

  const onMoreSettingsChange = (value, param) => {
    const tmpMoreSettings = { ...moreSettings };
    tmpMoreSettings[param] = value;
    setMoreSettings(tmpMoreSettings);
  };

  const getReportOnce = async () => {
    const data = await dashboardApiHandler.getLiveReports(
      project._id,
      processId
    );
    const report = data.reports.find((report) => report.robot.id === robot.id);

    if (report) {
      setLiveReport(report);

      setIsLoadingVoltages(false);
      setProcessId(null);
    }
  };

  const getLiveReports = (processId) => {
    window.clearInterval(liveInterval);

    if (!processId) return;

    setLiveReport(null);

    console.log("old liveInterval", liveInterval);

    let interval = window.setInterval(async () => {
      const data = await dashboardApiHandler.getLiveReports(
        project._id,
        processId
      );
      const report = data.reports.find(
        (report) => report.robot.id === robot.id
      );

      if (report) {
        setLiveReport(report);
        window.clearInterval(liveInterval);
        console.log("stopping getLiveReports", liveReport);
        setIsLoadingVoltages(false);
        setProcessId(null);
      }
    }, 3000);
    setLiveInterval(interval);
  };

  const saveSettings = async () => {
    try {
      const result = await apiHandler.setRobotSettings(robot.id, {
        settings,
        moreSettings,
        batteryPercentage,
      });

      const newSettings = { ...settings, ...result.settings };
      setSettings(newSettings);
      const newMoreSettings = { ...moreSettings, ...result.moreSettings };
      setMoreSettings(newMoreSettings);
      setRobot({
        ...robot,
        settings: newSettings,
        moreSettings: newMoreSettings,
      });
    } catch (error) {
      console.log(error);
      alert("Error saving robot settings");
    }

    setShowSaveSettings(false);
    setEditSettings(false);
  };

  const settingsInput = (param) => {
    return (
      <input
        disabled={!canEditSetting(param)}
        type="number"
        min={settings.minMax?.[param]?.min}
        max={settings.minMax?.[param]?.max}
        value={settings[param]}
        onChange={(e) => onChange(e.target.value, param)}
      />
    );
  };

  const onSaveClicked = () => {
    for (const key in settings) {
      if (!settings[key] || !settings.minMax[key]) continue;

      const { min, max } = settings?.minMax[key];
      if ((min && min > settings[key]) || (max && max < settings[key])) {
        alert(
          `The value of ${camelCaseToText(
            key
          )} should be in the range [${min}-${max}]`
        );
        return;
      }
    }

    setShowSaveSettings(true);
  };

  const downloadConfig = async () => {
    // Create the configuration object
    const config = {
      settings: { ...settings },
      moreSettings: {
        batterySize: moreSettings.batterySize,
      },
    };

    delete config.settings.minMax;
    delete config.settings.advanced;

    // Convert to JSON string
    const jsonString = JSON.stringify(config, null, 4);

    // Set default filename: projectName + robotName + config.json
    const defaultFilename = `${project.projectName} robot ${robot.robotName}.json`;

    try {
      // Try to use the File System Access API (Chrome/Edge)
      if ("showSaveFilePicker" in window) {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: defaultFilename,
          types: [
            {
              description: "JSON files",
              accept: {
                "application/json": [".json"],
              },
            },
          ],
        });

        const writable = await fileHandle.createWritable();
        await writable.write(jsonString);
        await writable.close();

        alert("Configuration exported successfully!");
      } else {
        // Fallback for browsers that don't support File System Access API
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = defaultFilename;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error saving file:", error);
        alert("Error saving configuration file. Please try again.");
      }
      // If user cancels the dialog, do nothing (AbortError)
    }
  };

  const processConfigFile = async (file) => {
    // Check if file is JSON
    if (!file.name.endsWith(".json")) {
      alert("Please select a JSON file");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const jsonContent = JSON.parse(e.target.result);

        // Validate that the JSON has the expected structure
        if (!jsonContent.settings && !jsonContent.moreSettings) {
          alert(
            "Invalid configuration file. Expected settings and/or moreSettings."
          );

          return;
        }

        // Update local state
        if (jsonContent.settings) {
          const tmpSettings = { ...settings };
          for (const key in jsonContent.settings) {
            tmpSettings[key] = jsonContent.settings[key];
          }

          setSettings(tmpSettings);
        }
        if (jsonContent.moreSettings) {
          const tmpMoreSettings = { ...moreSettings };
          for (const key in jsonContent.moreSettings) {
            tmpMoreSettings[key] = jsonContent.moreSettings[key];
          }
          setMoreSettings(tmpMoreSettings);
        }

        // Show success message
        setUploadSuccess(true);

        // Reset success message after 3 seconds
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000);
      } catch (error) {
        console.error("Error parsing or uploading configuration:", error);
        alert(
          "Error reading configuration file. Please check the file format."
        );
      }
    };

    reader.readAsText(file);
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processConfigFile(file);
    }
    // Reset the file input so the same file can be selected again
    e.target.value = "";
  };

  const onCancelClicked = () => {
    setEditSettings(false);
    setSettings(robot.settings);
    setMoreSettings(robot.moreSettings || {});
    setShowLoadDefault(false);
  };

  return (
    <>
      <div className={`robot-settings`}>
        {showSaveSettings ? (
          <Modal
            title={`Save robot settings?`}
            onClose={() => setShowSaveSettings(false)}
          >
            <div className="buttons-wrapper">
              <div
                className="button cancel"
                onClick={() => setShowSaveSettings(false)}
              >
                Cancel
              </div>
              <div className="button" onClick={saveSettings}>
                Continue
              </div>
            </div>
          </Modal>
        ) : null}

        {/* Hidden file input for config upload */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept=".json,application/json"
          style={{ display: "none" }}
        />

        <div className="title">
          {user.role === "projectOwner" ? (
            <span className="text">SETTINGS</span>
          ) : (
            <div className="settings-actions">
              {editSettings ? (
                <>
                  <span className="custom" onClick={onSaveClicked}>
                    SAVE SETTINGS
                  </span>
                  <span className="separator">|</span>
                  <span className="custom cancel" onClick={onCancelClicked}>
                    CANCEL
                  </span>
                </>
              ) : (
                <span className="custom" onClick={() => setEditSettings(true)}>
                  EDIT SETTINGS
                </span>
              )}

              {showLoadDefault && editSettings ? (
                <span
                  className="custom show-default"
                  onClick={loadDefaultSettings}
                >
                  <span className="separator">|</span>LOAD DEFAULT SETTINGS
                </span>
              ) : null}

              <span className="separator">|</span>

              {!editSettings && (
                <div
                  className="config-button download-button"
                  onClick={downloadConfig}
                  // title="drag and drop to upload config file"
                >
                  <img src={download} alt="Download" />
                  <span className="button-text">EXPORT CONFIG</span>
                </div>
              )}

              {editSettings && (
                <div
                  className="config-button download-button"
                  onClick={triggerFileUpload}
                  // title="drag and drop to upload config file"
                >
                  <img src={upload} alt="Upload" />
                  <span className="button-text">IMPORT CONFIG</span>
                </div>
              )}

              {uploadSuccess && (
                <span
                  className="upload-success"
                  style={{ color: "green", marginLeft: "10px" }}
                >
                  Configuration loaded successfully!
                </span>
              )}
            </div>
          )}

          {user.role !== "projectOwner" &&
          project.connectionStatus !== "offline" ? (
            <div className="refresh">
              {isLoadingVoltages && (
                <span className="loading">
                  <img src={spinner} /> GETTING VOLTAGE...
                </span>
              )}
              {!isLoadingVoltages && (
                <>
                  {liveReport ? (
                    <>
                      <span className="text">
                        VOLTAGE: {liveReport.voltage}V
                      </span>
                      {project.settings.robotVersion === 4 && (
                        <>
                          {" "}
                          {"|"}{" "}
                          {/* <span className="text">
                            DISTANCE POSSIBLE:{" "}
                            {Math.round(liveReport.distancePossible / 1000)}m
                          </span>{" "}
                          {"|"}{" "} */}
                          <span className="text">
                            CHARGING CURRENT: {liveReport.avgChargingCurrent}A
                          </span>{" "}
                          {"|"}{" "}
                          <span className="text">
                            BATTERY LEVEL: {liveReport.batteryPercentage}%
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="text" onClick={refreshRobotBatteries}>
                      GET UPDATED VOLTAGE
                    </span>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
        <div className="separator"></div>

        {settings ? (
          <div className="columns">
            <table
              className={
                editSettings ? "settings table1" : "settings disabled table1"
              }
            >
              <tbody>
                <tr className="header">
                  <td></td>
                  <td>Cleaning Mode</td>
                  <td>Transit Mode</td>
                </tr>
                <tr>
                  <td>Drive speed [m/minute]</td>
                  <td>{settingsInput("driveSpeedCleaning")}</td>
                  <td>{settingsInput("driveSpeedTransit")}</td>
                </tr>
                <tr>
                  <td>Rotor speed [RPM]</td>
                  <td>{settingsInput("rotorSpeedCleaning")}</td>
                  <td>{settingsInput("rotorSpeedTransit")}</td>
                </tr>

                <tr>
                  <td>Stop Distance [mm]</td>
                  <td>{settingsInput("stopDistanceCleaning")}</td>
                  <td>{settingsInput("stopDistanceTransit")}</td>
                </tr>
              </tbody>
            </table>

            <table
              className={
                editSettings ? "settings table2" : "settings disabled table2"
              }
            >
              <tbody>
                <tr className="header">
                  <td></td>
                  <td>Drive Motor</td>
                  <td>Cleaning Motor</td>
                </tr>
                <tr>
                  <td>Engine Type</td>
                  <td>
                    <Dropdown
                      disabled={!canEditSetting("driveMotorType")}
                      options={motorOptions.driveMotorOptions}
                      onChange={(option) =>
                        onChange(option.value, "driveMotorType")
                      }
                      value={settings.driveMotorType}
                      className="dropdown motor-type"
                      controlClassName="control"
                      placeholderClassName="placeholder"
                    />
                  </td>
                  <td>
                    <Dropdown
                      disabled={!canEditSetting("cleaningMotorType")}
                      options={motorOptions.cleaningMotorOptions}
                      onChange={(option) =>
                        onChange(option.value, "cleaningMotorType")
                      }
                      value={settings.cleaningMotorType}
                      className="dropdown motor-type"
                      controlClassName="control"
                      placeholderClassName="placeholder"
                    />
                  </td>
                </tr>
                <tr>
                  <td>0.5 sec current threshold [Amp]</td>
                  <td>{settingsInput("halfSecCurrentThresholdDriveMotor")}</td>
                  <td>
                    {settingsInput("halfSecCurrentThresholdCleaningMotor")}
                  </td>
                </tr>
                <tr>
                  <td>2 sec current threshold [Amp]</td>
                  <td>{settingsInput("twoSecCurrentThresholdDriveMotor")}</td>
                  <td>
                    {settingsInput("twoSecCurrentThresholdCleaningMotor")}
                  </td>
                </tr>
                <tr>
                  <td>Min % - real RPM/target</td>
                  <td>{settingsInput("rpmTargetRatioDriveMotor")}</td>
                  <td>{settingsInput("rpmTargetRatioCleaningMotor")}</td>
                </tr>
                <tr>
                  <td>CBC overcurrent threshold [Amp]</td>
                  <td>{settingsInput("driveMotorCBCovercurrentThresholdAMP")}</td>
                  <td>{settingsInput("cleanMotorCBCovercurrentThresholdAMP")}</td>
                </tr>
                <tr>
                  <td>Max stall events</td>
                  <td>{settingsInput("driveMotorStallDetectMaxEvents")}</td>
                  <td>{settingsInput("cleanMotorStallDetectMaxEvents")}</td>
                </tr>
              </tbody>
            </table>

            <table
              className={
                editSettings ? "settings table3" : "settings disabled table3"
              }
            >
              <tbody>
                <tr className="header">
                  <td></td>
                  <td>Value</td>
                </tr>
                <tr>
                  <td>Rotor 5 sec speed ratio [%]</td>
                  <td>
                    {settingsInput("cleanMotorSpeedRatioWhileTestingPERC")}
                  </td>
                </tr>
                {project.settings.robotVersion === 4 && (
                  <tr>
                    <td>Updated Battery Level [%]</td>
                    <td>
                      <input
                        disabled={!canEditBatteryPercentage}
                        type="number"
                        min="0"
                        max="100"
                        value={batteryPercentage}
                        onChange={(e) =>
                          setBatteryPercentage(Number(e.target.value))
                        }
                      />
                    </td>
                  </tr>
                )}

                <tr>
                  <td>Battery optimized threshold - high load [V]</td>
                  <td>{settingsInput("batteryThresholdHighLoad")}</td>
                </tr>

                <tr>
                  <td>Battery optimized threshold - low load [V]</td>
                  <td>{settingsInput("batteryThresholdLowLoad")}</td>
                </tr>

                <tr>
                  <td>Reserve battery threshold [Ah]</td>
                  <td>{settingsInput("reserveBatteryThresholdAh")}</td>
                </tr>

                <tr>
                  <td>Battery threshold - old [V]</td>
                  <td>{settingsInput("batteryVoltageThreshold")}</td>
                </tr>

                <tr>
                  <td>Battery Size [Ah]</td>
                  <td>
                    <Dropdown
                      disabled={!canEditBatterySize}
                      options={[
                        { value: "9", label: "9" },
                        { value: "12", label: "12" },
                        { value: "18", label: "18" },
                        { value: "24", label: "24" },
                        { value: "30", label: "30" },
                      ]}
                      onChange={(option) =>
                        onMoreSettingsChange(option.value, "batterySize")
                      }
                      value={moreSettings.batterySize?.toString()}
                      className="dropdown"
                      controlClassName="control"
                      placeholderClassName="placeholder"
                    />
                  </td>
                </tr>

                <tr></tr>

                <tr>
                  <td>Double cleaning distance in row edges [m]</td>
                  <td>{settingsInput("rowEdgesDoubleCleaningDistance")}</td>
                </tr>

                <tr>
                  <td>Clean motor min expected current [Amp]</td>
                  <td>{settingsInput("cleanMotorMinExpectedCurrentAMP")}</td>
                </tr>

                <tr>
                  <td>Wheel Diameter (mm)</td>
                  <td>
                    <Dropdown
                      disabled={!canEditSetting("driveWheelsDiameterMM")}
                      options={[
                        { value: "100", label: "100" },
                        { value: "125", label: "125" },
                      ]}
                      onChange={(option) =>
                        onChange(Number(option.value), "driveWheelsDiameterMM")
                      }
                      value={
                        settings.driveWheelsDiameterMM !== undefined
                          ? settings.driveWheelsDiameterMM.toString()
                          : (
                              project.settings.driveWheelsDiameterMM || 100
                            ).toString()
                      }
                      className="dropdown"
                      controlClassName="control"
                      placeholderClassName="placeholder"
                    />
                  </td>
                </tr>

                <tr>
                  <td>Actuator Extend time - lock [Sec]</td>
                  <td>{settingsInput("actuatorExtendTimeLock")}</td>
                </tr>

                <tr>
                  <td>Actuator Extend time - unLock [Sec]</td>
                  <td>{settingsInput("actuatorExtendTimeUnlock")}</td>
                </tr>

                <tr>
                  <td>Fast drying</td>
                  <td className="checkbox-wrapper">
                    <span className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        disabled={!canEditSetting("fastDrying")}
                        checked={settings.fastDrying}
                        onChange={(e) =>
                          onChange(e.target.checked, "fastDrying")
                        }
                      />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Fix charging during battery test</td>
                  <td className="checkbox-wrapper">
                    <span className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        disabled={!canEditSetting("chargeFixDuringBatteryTest")}
                        checked={settings.chargeFixDuringBatteryTest}
                        onChange={(e) =>
                          onChange(
                            e.target.checked,
                            "chargeFixDuringBatteryTest"
                          )
                        }
                      />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Fix Jammed robot on table</td>
                  <td className="checkbox-wrapper">
                    <span className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        disabled={!canEditSetting("jammedRobotFixOnTable")}
                        checked={settings.jammedRobotFixOnTable}
                        onChange={(e) =>
                          onChange(e.target.checked, "jammedRobotFixOnTable")
                        }
                      />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Ticks between stall tests (100ms)</td>
                  <td>{settingsInput("ticksForStallDetection")}</td>
                </tr>
                <tr>
                  <td>Clean motor stall speed [RPM]</td>
                  <td>{settingsInput("cleanMotorStallDetectSpeedRPM")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default RobotSettings;
